import { useEffect, useMemo, useState } from 'react';
import { DashboardRepository } from '../../data/repositories/DashboardRepository';
import {
  formatHabitaDateInput,
  formatHabitaDateRangeLabel,
  HabitaDateRangeFilter,
  HabitaEmptyState,
  HabitaErrorNotice,
  isHabitaDateInputValue,
  resolveHabitaDateRange,
} from '../../shared/components/habita';
import type { HabitaDatePeriod, HabitaDateRangeFilterValue } from '../../shared/components/habita';
import type { DashboardData } from '../../shared/models';
import {
  getSdrResponsibleEmail,
  isSdrScopedUser,
} from '../../shared/utils';
import {
  DashboardActivities,
  DashboardFunnel,
  DashboardLoadingState,
  DashboardMetricCard,
} from './components/DashboardWidgets';

const DASHBOARD_DATE_FILTER_STORAGE_KEY = 'habita4u.dashboard.dateFilter';

const dateFilterPeriods: HabitaDatePeriod[] = [
  'today',
  'yesterday',
  '7d',
  '30d',
  '90d',
  'month',
  'previous_month',
  'previous_quarter',
  'year',
  'custom',
];

const defaultDateFilterValue: HabitaDateRangeFilterValue = {
  customEnd: '',
  customStart: '',
  period: '30d',
};

function defaultDateFilter(): HabitaDateRangeFilterValue {
  return { ...defaultDateFilterValue };
}

function savedDashboardDateFilter(): HabitaDateRangeFilterValue | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return null;

  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(DASHBOARD_DATE_FILTER_STORAGE_KEY) ?? 'null'
    ) as Partial<HabitaDateRangeFilterValue> | null;
    if (!parsed) return null;

    const savedPeriod = parsed.period;
    if (!savedPeriod || !dateFilterPeriods.includes(savedPeriod)) return null;

    if (savedPeriod === 'custom') {
      if (
        !isHabitaDateInputValue(parsed.customStart) ||
        !isHabitaDateInputValue(parsed.customEnd)
      ) {
        return null;
      }
      if (parsed.customStart > parsed.customEnd) return null;

      return {
        customEnd: parsed.customEnd,
        customStart: parsed.customStart,
        period: 'custom',
      };
    }

    return {
      customEnd: '',
      customStart: '',
      period: savedPeriod,
    };
  } catch {
    return null;
  }
}

function initialDateFilter(): HabitaDateRangeFilterValue {
  return savedDashboardDateFilter() ?? defaultDateFilter();
}

function maxDateInputValue(data: DashboardData | null): string {
  const today = new Date();
  const generatedAt = data ? new Date(data.generatedAt) : null;
  const maxDate =
    generatedAt && !Number.isNaN(generatedAt.getTime()) && generatedAt > today
      ? generatedAt
      : today;

  return formatHabitaDateInput(maxDate);
}

export default function Dashboard() {
  const dashboardRepository = useMemo(() => new DashboardRepository(), []);
  const isResponsibleScoped = useMemo(() => isSdrScopedUser(), []);
  const responsibleScopeEmail = useMemo(() => getSdrResponsibleEmail(), []);
  const [data, setData] = useState<DashboardData | null>(null);
  const [dateFilter, setDateFilter] = useState<HabitaDateRangeFilterValue>(initialDateFilter);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestRange = useMemo(
    () => resolveHabitaDateRange(dateFilter),
    [dateFilter]
  );
  const hasInvalidCustomRange = Boolean(
    dateFilter.period === 'custom' &&
      dateFilter.customStart &&
      dateFilter.customEnd &&
      dateFilter.customStart > dateFilter.customEnd
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;

    window.sessionStorage.setItem(
      DASHBOARD_DATE_FILTER_STORAGE_KEY,
      JSON.stringify({
        customEnd: dateFilter.period === 'custom' ? dateFilter.customEnd : '',
        customStart: dateFilter.period === 'custom' ? dateFilter.customStart : '',
        period: dateFilter.period,
      })
    );
  }, [dateFilter.customEnd, dateFilter.customStart, dateFilter.period]);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      if (hasInvalidCustomRange) {
        if (!cancelled) {
          setLoading(false);
          setError(null);
          setData(null);
        }
        return;
      }

      if (isResponsibleScoped && !responsibleScopeEmail) {
        if (!cancelled) {
          setLoading(false);
          setError('Não foi possível identificar o responsável do usuário logado.');
          setData(null);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const dashboard = await dashboardRepository.fetchDashboard({
          startDate: requestRange.startDate,
          endDate: requestRange.endDate,
          ...(isResponsibleScoped && responsibleScopeEmail ? { responsibleEmail: responsibleScopeEmail } : {}),
        });
        if (!cancelled) {
          setData(dashboard);
        }
      } catch (loadError) {
        console.error('Erro ao carregar dashboard:', loadError);
        if (!cancelled) {
          setError('Não foi possível carregar os dados do dashboard.');
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [
    dashboardRepository,
    hasInvalidCustomRange,
    isResponsibleScoped,
    requestRange.endDate,
    requestRange.startDate,
    responsibleScopeEmail,
  ]);

  const dashboardView = useMemo(() => {
    if (!data) return null;

    return {
      data,
      rangeLabel: formatHabitaDateRangeLabel(requestRange),
    };
  }, [data, requestRange]);
  const maxSelectableDate = maxDateInputValue(data);

  return (
    <section className="w-full space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-fraunces text-2xl font-semibold text-textPrimary">
            Visão geral
          </h1>
          <p className="mt-1 max-w-2xl font-instrument text-sm text-textSecondary">
            Monitore captura, validação e prontidão das pastas dos clientes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HabitaDateRangeFilter
            value={dateFilter}
            onApply={setDateFilter}
            onClear={() => setDateFilter(defaultDateFilter())}
            maxDateInput={maxSelectableDate}
          />
        </div>
      </div>

      {hasInvalidCustomRange && (
        <HabitaErrorNotice>
          A data inicial precisa ser anterior ou igual à data final.
        </HabitaErrorNotice>
      )}

      {dashboardView && (
        <p className="font-instrument text-xs text-textSecondary">
          Exibindo dados de:{' '}
          <span className="font-medium text-textPrimary">{dashboardView.rangeLabel}</span>
        </p>
      )}

      {error && (
        <HabitaErrorNotice>{error}</HabitaErrorNotice>
      )}

      {loading ? (
        <DashboardLoadingState />
      ) : dashboardView ? (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {dashboardView.data.metrics.map((metric) => (
              <DashboardMetricCard key={metric.id} metric={metric} />
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <DashboardFunnel stages={dashboardView.data.funnel} />
            </div>
            <DashboardActivities activities={dashboardView.data.recentActivities} />
          </div>
        </>
      ) : hasInvalidCustomRange ? (
        null
      ) : (
        <HabitaEmptyState
          icon="ri-dashboard-line"
          title="Nenhum dado disponível"
          description="Não há dados operacionais para exibir neste momento."
        />
      )}
    </section>
  );
}
